import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { NegotiationDialogApi } from './negotiation-dialog-api';

export const ANONYMOUS_NEGOTIATION_ENDPOINTS = {
  customers: '/anonymous/dc-customers',
  formModel: '/anonymous/dc/document-type-enterprises/generate-form/',
  documentTemplate: '/anonymous/dc/document-type-enterprises/get-by-id/',
  templateFiles: '/anonymous/dc/template-files/get-all',
  merge: '/anonymous/dc/files/merge-data-to-file',
  upload: '/anonymous/contract/upload/file',
  update: '/anonymous/negotiate/form/update',
  certificateType: '/common/item-type/enterprise-certificate-type',
  representativeCertificateType: '/common/item-type/representative-certificate-type'
};

export interface AnonymousNegotiationContext {
  hashKey: string;
  tenantId: string;
  contractId: string;
  pdfFilePath?: string;
}

@Injectable({ providedIn: 'root' })
export class AnonymousNegotiationService {
  constructor(private apiService: ApiService) {}

  getFormModel(context: AnonymousNegotiationContext, templateId: string): Observable<any> {
    const params = new HttpParams()
      .set('hashKey', context.hashKey)
      .set('tenantId', context.tenantId);
    return this.apiService.get(ANONYMOUS_NEGOTIATION_ENDPOINTS.formModel + encodeURIComponent(templateId), params);
  }

  getDocumentTemplate(context: AnonymousNegotiationContext, templateId: string): Observable<any> {
    const params = new HttpParams()
      .set('hashKey', context.hashKey)
      .set('tenantId', context.tenantId);
    return this.apiService.get(ANONYMOUS_NEGOTIATION_ENDPOINTS.documentTemplate + encodeURIComponent(templateId), params);
  }

  getTemplateSource(context: AnonymousNegotiationContext, templateId: string): Observable<any> {
    return this.getDocumentTemplate(context, templateId).pipe(
      switchMap(template => {
        const documentTemplateId = template && template.data && template.data.documentTemplateId;
        if (!template || template.success === false || documentTemplateId == null) {
          return throwError(new Error('Unable to load anonymous document template'));
        }
        return this.getTemplateFiles(context, documentTemplateId);
      }),
      map(files => {
        const templateFile = files && files.data && files.data[0];
        const url = templateFile && templateFile.file && templateFile.file.url;
        if (!files || files.success === false || !url) {
          throw new Error('Unable to load anonymous template source file');
        }
        return { ...files, data: { url } };
      })
    );
  }

  getTemplateFiles(context: AnonymousNegotiationContext, templateId: number): Observable<any> {
    const params = new HttpParams()
      .set('templateId', String(templateId))
      .set('hashKey', context.hashKey);
    return this.apiService.get(ANONYMOUS_NEGOTIATION_ENDPOINTS.templateFiles, params);
  }

  getCustomers(context: AnonymousNegotiationContext, ids: string[]): Observable<any> {
    return this.apiService.post(ANONYMOUS_NEGOTIATION_ENDPOINTS.customers, {
      hashKey: context.hashKey,
      tenantId: context.tenantId,
      ids
    });
  }

  getCertificateType(): Observable<any> {
    return this.apiService.get(ANONYMOUS_NEGOTIATION_ENDPOINTS.certificateType);
  }

  getRepresentativeCertificateType(): Observable<any> {
    return this.apiService.get(ANONYMOUS_NEGOTIATION_ENDPOINTS.representativeCertificateType);
  }

  mergeDataToFile(context: AnonymousNegotiationContext, body: any): Observable<Blob> {
    const params = new HttpParams()
      .set('hashKey', context.hashKey)
      .set('tenantId', context.tenantId);
    return this.apiService.getFilePostMethod(ANONYMOUS_NEGOTIATION_ENDPOINTS.merge, body, params);
  }

  uploadFile(context: AnonymousNegotiationContext, file: File): Observable<any> {
    const form = new FormData();
    form.append('file', file);
    const params = new HttpParams().set('hashKey', context.hashKey);
    return this.apiService.postUpload(ANONYMOUS_NEGOTIATION_ENDPOINTS.upload + '?' + params.toString(), form);
  }

  updateForm(context: AnonymousNegotiationContext, body: any): Observable<any> {
    return this.apiService.post(ANONYMOUS_NEGOTIATION_ENDPOINTS.update, {
      contractId: context.contractId,
      filePath: body.filePath,
      hashKey: context.hashKey,
      fields: body.fields
    });
  }

  // Bind the anonymous link context for the shared popup; all operations call service APIs.
  createApi(context: AnonymousNegotiationContext): NegotiationDialogApi {
    return {
      getFormModel: templateId => this.getFormModel(context, templateId),
      getTemplateSource: templateId => this.getTemplateSource(context, templateId),
      getCustomers: ids => this.getCustomers(context, ids),
      getCertificateType: () => this.getCertificateType(),
      getRepresentativeCertificateType: () => this.getRepresentativeCertificateType(),
      mergeDataToFile: body => this.mergeDataToFile(context, body),
      uploadFile: file => this.uploadFile(context, file),
      updateForm: body => this.updateForm(context, body)
    };
  }
}
